/**
 * Quiet Hours palette — the source of truth for the experience's TS-side
 * colors. CSS variables in `Hub.css`, `SessionConfig.css`, `Session.css`
 * own the *names* (`--qh-paper`, `--qh-ink`, etc); this file is the
 * canonical mapping used in JS (motion springs, filter primitives that
 * need a real hex).
 *
 * Two slices: light = "early day" / "pale field." dark = "lamp room" /
 * "near-night." Same key shape so callers do `palette[theme].x`.
 *
 * Compared with the Reading Room palette: cooler, more muted. There is
 * no terracotta here. There is no warm gold. Quiet Hours leans into
 * pale fields, soft ocean, gentle dawn, lamp amber for evening, and a
 * cool slate for sleep.
 */

export interface QuietHoursPalette {
  /** Background field (the "room"). */
  paper: string;
  paperDeep: string;
  /** Text + soft text. */
  ink: string;
  inkSoft: string;
  inkFaint: string;
  /** Category accent strips — used very sparingly. */
  morning: string;
  evening: string;
  anxiety: string;
  sleep: string;
  /** Soft luminous orb glow (a warm cream in light, warm ember in dark). */
  orbGlow: string;
  /** Borders/dividers — almost-invisible hairlines. */
  hairline: string;
}

export const PALETTE_LIGHT: QuietHoursPalette = {
  paper: '#F0E9DC',
  paperDeep: '#E2D9C5',
  ink: '#2E2A24',
  inkSoft: '#6A6055',
  inkFaint: '#9B8F7E',
  morning: '#E8C088',
  evening: '#D6A06A',
  anxiety: '#8FA8B0',
  sleep: '#A89FB0',
  orbGlow: '#F7E2B6',
  hairline: 'rgba(46, 42, 36, 0.14)',
};

export const PALETTE_DARK: QuietHoursPalette = {
  paper: '#171410',
  paperDeep: '#0E0C09',
  ink: '#E8DCC8',
  inkSoft: '#B0A088',
  inkFaint: '#7A6E5E',
  morning: '#E0B27A',
  evening: '#D89858',
  anxiety: '#7A98A2',
  sleep: '#8A7FA0',
  orbGlow: '#FFCC88',
  hairline: 'rgba(232, 220, 200, 0.10)',
};

export const palette = {
  light: PALETTE_LIGHT,
  dark: PALETTE_DARK,
} as const;

export type QuietHoursTheme = keyof typeof palette;

export function paletteFor(theme: QuietHoursTheme): QuietHoursPalette {
  return palette[theme];
}
